import { locations } from './propertyConfig';

const parseImagesField = (imagesField) => {
  if (Array.isArray(imagesField)) {
    return imagesField;
  }

  if (typeof imagesField === 'string') {
    const trimmed = imagesField.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  return [];
};

const normalizeImageValue = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'object') {
    return String(
      value.mediumUrl ||
        value.medium_url ||
        value.url ||
        value.thumbnailUrl ||
        value.thumbnail_url ||
        ''
    ).trim();
  }

  return '';
};

export const isTruthy = (value) => value === true || value === 'true' || value === 1;

export const getPropertyState = (property) => {
  if (!property || typeof property !== 'object') {
    return '';
  }
  if (property.state) {
    return property.state;
  }
  const text = String(property.location || '').toLowerCase();
  const match = locations.find((state) => text.includes(state.toLowerCase()));
  if (match) {
    return match;
  }
  return text.includes('abuja') ? 'FCT' : '';
};

export const getPropertyArea = (property) => {
  if (!property || typeof property !== 'object') {
    return '';
  }
  if (property.area) {
    return property.area;
  }
  const locationText = String(property.location || '');
  const parts = locationText
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length > 1) {
    return parts[0];
  }
  return locationText;
};

export const getPropertyAddress = (property) => {
  if (!property || typeof property !== 'object') {
    return '';
  }
  // Return the detailed street address if available
  if (property.address) {
    return property.address;
  }
  // Fallback to location field
  return property.location || '';
};

/**
 * Guaranteed-descriptive SEO alt text for a property image, e.g.
 * "3 bedroom apartment for sale in Lekki, Lagos". Built from structured
 * fields the same way generatePropertySlug (seoUtils.js) is, rather than
 * trusting the free-text listing title, which agents sometimes leave
 * generic ("Nice House") or blank.
 */
export const getPropertyAltText = (property) => {
  if (!property || typeof property !== 'object') {
    return 'Property listing in Nigeria';
  }

  const parts = [];

  if (property.bedrooms) {
    parts.push(`${property.bedrooms} bedroom`);
  }

  const propertyType = property.propertyType || property.property_type || 'property';
  parts.push(propertyType.toLowerCase());

  const listingType = property.listing_type || property.listingType || property.type || 'rent';
  if (listingType === 'buy') {
    parts.push('for sale');
  } else if (listingType === 'short-let') {
    parts.push('short-let');
  } else {
    parts.push('for rent');
  }

  const location = [getPropertyArea(property), getPropertyState(property)]
    .filter(Boolean)
    .join(', ');
  if (location) {
    parts.push(`in ${location}`);
  }

  return parts.join(' ');
};

export const getPrimaryImage = (property, fallback = '') => {
  if (!property) {
    return fallback;
  }

  if (property.image) {
    return normalizeImageValue(property.image);
  }

  const images = parseImagesField(property.images);
  if (images.length > 0) {
    const firstImage = images.map(normalizeImageValue).find(Boolean);
    if (firstImage) {
      return firstImage;
    }
  }

  return fallback;
};

/**
 * Render a date as "3 hours ago" / "2 days ago" etc. Returns null when the
 * date is missing/invalid so callers can decide their own fallback text.
 */
export const getRelativeTimeAgo = (dateSource) => {
  if (!dateSource) {
    return null;
  }
  const time = new Date(dateSource).getTime();
  if (!Number.isFinite(time)) {
    return null;
  }

  const minutes = Math.max(1, Math.floor((Date.now() - time) / 60000));
  if (minutes < 60) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

/**
 * Render a date as "12 Jul" for compact availability-window display.
 * Returns null when the date is missing/invalid.
 */
export const formatShortDate = (dateSource) => {
  if (!dateSource) {
    return null;
  }
  const date = new Date(dateSource);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export const getPropertyImages = (property, fallback = '') => {
  if (!property || typeof property !== 'object') {
    return fallback ? [fallback] : [];
  }

  const images = [property.image, ...parseImagesField(property.images)]
    .map(normalizeImageValue)
    .filter(Boolean);

  const deduped = [...new Set(images)];
  if (deduped.length) {
    return deduped;
  }

  return fallback ? [fallback] : [];
};

// ===== Duration Conversion Utilities =====
/**
 * Convert months to years with 2 decimal precision
 * @param {number} months - Number of months
 * @returns {string} Number of years as string with up to 2 decimals
 */
export const monthsToYears = (months) => {
  if (!months || isNaN(months)) {
    return '0';
  }
  const years = months / 12;
  // Return with 2 decimals, but remove trailing zeros
  return parseFloat(years.toFixed(2)).toString();
};

// ===== Short-let FAQs & booking policy =====

/**
 * One flat rule for every short-let booking (no per-listing customization
 * yet). Shared between the FAQ answer below and the booking cancel flow so
 * the same wording appears in both places.
 */
export const CANCELLATION_POLICY_TEXT =
  'Free cancellation up to 48 hours before check-in. No refund for cancellations made within 48 hours of check-in.';

const formatNaira = (amount) => `₦${Number(amount).toLocaleString('en-NG')}`;

const CURRENCY_SYMBOLS = { NGN: '₦', USD: '$', GBP: '£', EUR: '€', CAD: 'CA$' };

/**
 * Formats an NGN-denominated amount for buyer-facing display in the
 * currently selected currency. Always a full replacement (e.g. "$32,450"),
 * never "₦50,000,000 (≈ $32,450)". Rounds to whole units, no decimals.
 * Takes the useCurrency() return value directly so call sites don't need to
 * know the shape of the rates object, and inherits its NGN fallback
 * whenever rates aren't ready.
 */
export const formatPrice = (amountInNgn, { currency, convert }) => {
  const { value, currency: resolvedCurrency } = convert(amountInNgn);
  const symbol = CURRENCY_SYMBOLS[resolvedCurrency] || CURRENCY_SYMBOLS.NGN;
  return `${symbol}${Math.round(value).toLocaleString('en-US')}`;
};

/**
 * Derives FAQ question/answer pairs from fields the property already has,
 * rather than free-text authoring - keeps every listing's FAQ section
 * accurate without the seller having to write it.
 */
export const buildPropertyFaqs = (property, currencyCtx) => {
  if (!property) {
    return [];
  }
  const faqs = [];

  const checkIn = property.check_in_time;
  const checkOut = property.check_out_time;
  if (checkIn || checkOut) {
    faqs.push({
      question: 'What are the check-in and check-out times?',
      answer: `Check-in is ${checkIn || 'flexible, contact the host'}, check-out is ${
        checkOut || 'flexible, contact the host'
      }.`,
    });
  }

  const maxGuests = property.max_guests;
  if (maxGuests) {
    faqs.push({
      question: 'How many guests can stay?',
      answer: `This property comfortably fits up to ${maxGuests} guest${maxGuests === 1 ? '' : 's'}.`,
    });
  }

  const minNights = property.min_nights;
  if (minNights && Number(minNights) > 1) {
    faqs.push({
      question: 'Is there a minimum stay?',
      answer: `Yes, a minimum of ${minNights} nights per booking.`,
    });
  }

  const depositAmount = property.damage_deposit_amount;
  faqs.push({
    question: 'Is there a damage deposit?',
    answer: depositAmount
      ? `Yes, a refundable damage deposit of ${
        currencyCtx ? formatPrice(depositAmount, currencyCtx) : formatNaira(depositAmount)
      }${property.damage_deposit_notes ? ` (${property.damage_deposit_notes})` : ''}.`
      : 'No damage deposit is required for this property.',
  });

  faqs.push({
    question: 'Can I cancel my booking?',
    answer: CANCELLATION_POLICY_TEXT,
  });

  return faqs;
};
